import { parseHTML } from '@/lib/tiptap';
import {
  type AgendaItemNote,
  AttendanceStatus,
  type Maybe,
  type Meeting,
  MeetingCategory,
  MeetingEmail,
  type MeetingService,
  type TeamMember,
  TeamMemberActive,
} from '../../../graphql/types';
import { displayDate } from '../../../helpers/date-helpers';
import { ReportUrls } from './report-urls';

export * as Email from './email';

export type CreateMailtoArgs = {
  from: string;
  meeting: Meeting;
  meetingTools: MeetingService;
  isAutomatedReport?: boolean;
};

export { REPORT_FROM_EMAIL } from '../../../helpers/company-branding';

export const createMailtoLink = ({
  from,
  meeting,
  meetingTools,
  isAutomatedReport,
}: CreateMailtoArgs) => {
  const recipients = sendTo(meeting, meetingTools.hotelTeams, isAutomatedReport);
  const emailSubject = subject(meeting, isAutomatedReport);
  const reportLink = shareLink(meeting.id!);

  const mailUrl = `mailto:${recipients.join(';')}?cc=${from}&subject=${encodeURIComponent(emailSubject)}`;

  const messageBody =
    `<b>Please click the <a href="${reportLink}">link</a> to view your report:</b>` +
    '<br>' +
    reportLink +
    '\n' +
    meetingDetails(meeting, meetingTools);

  return {
    emailSubject,
    mailUrl,
    messageBody,
    meetingDetails: meetingDetails(meeting, meetingTools),
    recipients,
    reportLink,
  };
};

const activeTeam = (member: Maybe<TeamMember>) => {
  return member?.active === TeamMemberActive.Active;
};

const attendanceStatusLabel = (status: Maybe<AttendanceStatus>) => {
  switch (status) {
    case AttendanceStatus.Attended:
      return 'Attended';
    case AttendanceStatus.Cancelled:
      return 'Cancelled';
    case AttendanceStatus.NoAttendees:
      return 'No Hotel Attendees';
    default:
      return 'Attended';
  }
};

const emailPref = (
  member: Maybe<TeamMember>,
  meeting: Maybe<Meeting>,
  isAutomatedReport?: boolean,
) => {
  // All users get everything including automated reports
  if (member?.emailPref === MeetingEmail.All) {
    return true;
  }

  // For automated reports (weekend, out-of-office, etc.), check automated-specific preferences
  if (isAutomatedReport || meeting?.category === MeetingCategory.Automated) {
    return (
      member?.emailPref === MeetingEmail.AutomatedOnly ||
      member?.emailPref === MeetingEmail.DailyAndAutomated ||
      member?.emailPref === MeetingEmail.WeeklyAndAutomated
    );
  }

  // For regular reports, check exact category match and combination preferences
  if (meeting?.category === MeetingCategory.Daily) {
    return (
      member?.emailPref === MeetingEmail.Daily ||
      member?.emailPref === MeetingEmail.DailyAndAutomated
    );
  }

  if (meeting?.category === MeetingCategory.Weekly) {
    return (
      member?.emailPref === MeetingEmail.Weekly ||
      member?.emailPref === MeetingEmail.WeeklyAndAutomated
    );
  }

  // For ADHOC or other categories, use exact match
  return member?.emailPref === meeting?.category;
};

const listAgenda = (
  ids: string[],
  meetingTools: MeetingService,
  itemNotes?: Maybe<Maybe<AgendaItemNote>[]>,
) => {
  if (ids.length > 0) {
    const items = ids
      .map((id) => {
        const agendaItem = meetingTools.agendaItems?.find((item) => String(item?.id) === id);
        const note = itemNotes?.find((item) => String(item?.itemId) === id);
        return `<li>${agendaItem?.name} ${
          note?.notes ? `<ul><li>${note.notes}</li></ul>` : ''
        }</li>`;
      })
      .filter((item) => item !== undefined)
      .join('\n');
    return `<ul>${items}</ul>`;
  }
  return '';
};

const listAttendees = (ids: string[], meetingTools: MeetingService) => {
  if (ids.length > 0) {
    const items = ids
      .map((id) => {
        const attendee = meetingTools?.hotelTeams?.find((item) => String(item?.id) === id);
        return `<li>${attendee?.name}</li>`;
      })
      .filter((item) => item !== undefined)
      .join('\n');
    return `<ul>${items}</ul>`;
  }
  return '';
};

const meetingDetails = (meeting: Maybe<Meeting>, meetingTools: Maybe<MeetingService>) => {
  let details = '';
  if (!meeting || !meetingTools) {
    return details;
  }

  // Add attendance status section only if not "Attended"
  const attendanceStatus = meeting.attendanceStatus || AttendanceStatus.Attended;
  if (attendanceStatus !== AttendanceStatus.Attended) {
    const attendanceStatusText = attendanceStatusLabel(attendanceStatus);
    details = `${details}<h3>--- Attendance Status ---</h3>\n${attendanceStatusText}\n\n`;
  }

  if (meeting.category === MeetingCategory.Weekly) {
    details =
      details +
      `<h3>--- Agenda ---</h3>` +
      '\n' +
      listAgenda(meeting.agendaItems as string[], meetingTools, meeting.agendaItemNotes) +
      '\n' +
      `<h3>--- Attendees ---</h3>` +
      '\n' +
      listAttendees(meeting.attendees as string[], meetingTools) +
      '\n';
  }

  details = `${details}<h3>--- Notes ---</h3>\n${notesText(meeting)}\n`;

  return details;
};

const notesText = (meeting: Meeting) => {
  return parseHTML(meeting.notes);
};

const sendTo = (
  meeting: Maybe<Meeting>,
  to?: Maybe<Maybe<TeamMember>[]>,
  isAutomatedReport?: boolean,
) => {
  if (to) {
    return to
      .filter(activeTeam)
      .filter((member) => emailPref(member, meeting, isAutomatedReport))
      .map((member) => member?.email);
  }
  return [];
};

const shareLink = (meetingId: string) => {
  return ReportUrls.createShareUrl(meetingId);
};

const subject = (meeting: Meeting, isAutomatedReport?: boolean) => {
  const { brandCode, meetingDate } = meeting;
  const category = isAutomatedReport ? 'Automated' : subjectCategory(meeting);
  return `${brandCode} | WorldHotels RM ${category} Update for ${displayDate(meetingDate || 'today')}`;
};

const subjectCategory = (meeting: Meeting) => {
  switch (meeting.category) {
    case MeetingCategory.Weekly:
      return 'Weekly';
    case MeetingCategory.Adhoc:
      return 'Ad Hoc';
    case MeetingCategory.Automated:
      return 'Automated';
    default:
      return 'Daily';
  }
};

// Export helper functions for weekend report processing
export { sendTo, emailPref };
